import React from 'react';
import {
  Box,
  CircularProgress,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { BRAZILIAN_STATES } from '@utils/brazilianStates';
import type { SignupFormValues } from '../../hooks/useSignupForm';

interface AddressFieldsProps {
  values: SignupFormValues;
  fieldErrors: Partial<Record<keyof SignupFormValues, string>>;
  handleChange: (field: keyof SignupFormValues) => (value: any) => void;
  buscandoCEP: boolean;
  onZipChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

/**
 * Campos de endereço (CEP, rua, número, complemento, bairro, cidade, estado) + site oficial.
 * Extraído de SignupForm.tsx (F7).
 */
export const AddressFields: React.FC<AddressFieldsProps> = ({
  values,
  fieldErrors,
  handleChange,
  buscandoCEP,
  onZipChange,
}) => {
  return (
    <>
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <Box sx={{ flex: '0 0 auto', minWidth: { xs: '100%', sm: '150px' }, maxWidth: { xs: '100%', sm: '200px' } }}>
            <TextField
              fullWidth
              label="CEP *"
              value={values.zipCode}
              onChange={onZipChange}
              placeholder="00000-000"
              inputProps={{ maxLength: 9 }}
              error={!!fieldErrors.zipCode}
              helperText={
                buscandoCEP
                  ? 'Buscando endereço...'
                  : fieldErrors.zipCode || 'Digite o CEP para preenchimento automático'
              }
              required
              InputProps={{
                endAdornment: buscandoCEP ? (
                  <InputAdornment position="end">
                    <CircularProgress size={20} />
                  </InputAdornment>
                ) : null,
              }}
            />
          </Box>
          <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', sm: '200px' } }}>
            <TextField
              fullWidth
              label="Rua *"
              value={values.street}
              onChange={(e) => handleChange('street')(e.target.value)}
              error={!!fieldErrors.street}
              helperText={fieldErrors.street || 'Digite o nome da rua'}
              required
            />
          </Box>
          <Box sx={{ flex: '0 0 auto', minWidth: { xs: '100%', sm: '120px' }, maxWidth: { xs: '100%', sm: '150px' } }}>
            <TextField
              fullWidth
              label="Número *"
              value={values.number}
              onChange={(e) => handleChange('number')(e.target.value)}
              error={!!fieldErrors.number}
              helperText={fieldErrors.number || 'Digite o número'}
              required
            />
          </Box>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', sm: '200px' } }}>
            <TextField
              fullWidth
              label="Complemento"
              value={values.complement}
              onChange={(e) => handleChange('complement')(e.target.value)}
              error={!!fieldErrors.complement}
              helperText={fieldErrors.complement || 'Apto, Bloco, etc. (opcional)'}
            />
          </Box>
          <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', sm: '200px' } }}>
            <TextField
              fullWidth
              label="Bairro *"
              value={values.neighborhood}
              onChange={(e) => handleChange('neighborhood')(e.target.value)}
              error={!!fieldErrors.neighborhood}
              helperText={fieldErrors.neighborhood || 'Digite o bairro'}
              required
            />
          </Box>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', sm: '300px' } }}>
            <TextField
              fullWidth
              label="Cidade *"
              value={values.city}
              onChange={(e) => handleChange('city')(e.target.value)}
              error={!!fieldErrors.city}
              helperText={fieldErrors.city || 'Digite a cidade'}
              required
            />
          </Box>
          <Box sx={{ flex: '0 0 auto', minWidth: { xs: '100%', sm: '150px' }, maxWidth: { xs: '100%', sm: '200px' } }}>
            <FormControl fullWidth required error={!!fieldErrors.state}>
              <InputLabel>Estado *</InputLabel>
              <Select
                value={values.state || ''}
                label="Estado *"
                onChange={(e) =>
                  handleChange('state')(typeof e.target.value === 'string' ? e.target.value : '')
                }
              >
                {BRAZILIAN_STATES.map((state) => (
                  <MenuItem key={state.value} value={state.value}>
                    {state.value}
                  </MenuItem>
                ))}
              </Select>
              {fieldErrors.state && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                  {fieldErrors.state}
                </Typography>
              )}
            </FormControl>
          </Box>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Site Oficial (Opcional)"
          value={values.website}
          onChange={(e) => handleChange('website')(e.target.value)}
          placeholder="https://www.exemplo.com.br"
          error={!!fieldErrors.website}
          helperText={fieldErrors.website || 'URL do site oficial da empresa'}
        />
      </Grid>
    </>
  );
};

export default AddressFields;
